import { Skeleton, SkeletonizedValue } from '@round/ui-kit';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import useProjectDetails from '../useProjectDetails';
import { useUpdateProjectMutation } from '@round/creatorbase-helpers/api-hooks';
import { useEffect, useState } from 'react';
import { asMoneyWithoutZeros, showNotification } from 'helpers';
import { AutoGrowthInput, ImageUpload } from '@round/whitelabel-ui';
import HeaderSummary from '../../../components/HeaderSummary/HeaderSummary';
import RunDates from '../../../components/RunDates/RunDates';
import moment from 'moment';
import CircleBadge from 'Modules/Plans/components/CircleBadge/CircleBadge';
import { getUserNameInitials } from 'Modules/Plans/User/user.helpers';
import useRoundPlan from '../components/RoundPlan/useRoundPlan';
import { ProtectedByUserGroups } from 'SharedComponents/ProtectedByUserGroup/ProtectedByUserGroups';
import ReportLink from '../components/ReportLink/ReportLink';
import ProjectRunBy from '../components/ProjectRunBy/ProjectRunBy';
import SongCard from '../components/SongCard/SongCard';
import RoundPlan from '../components/RoundPlan/RoundPlan';
import { creatorbase } from '@round/api';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import styles from './ProjectData.module.css';

type Props = {
    platform?: creatorbase.Platform;
    onSongClick?: (songId: number) => void;
};

const ProjectData = ({ platform, onSongClick }: Props) => {
    const { getCurrencyById } = useCurrencies();
    const { project, isLoading, status, brand, isBrandSuccess, team, isTeamSuccess, users, areUsersLoading } =
        useProjectDetails();
    const { update: updateProject, isLoading: isUpdateLoading } = useUpdateProjectMutation();

    const [projectName, setProjectName] = useState('');
    const cost = asMoneyWithoutZeros(project?.total_cost, getCurrencyById(project?.currency_id));

    useEffect(() => {
        if (project?.name) {
            setProjectName(project.name);
        }
    }, [project?.name]);

    const handleProjectNameSubmit = async () => {
        const name = projectName.trim();
        if (status !== 'success' || !project || name === project?.name) {
            return;
        }

        try {
            await updateProject({ id: project.id, name });
            showNotification('Project name updated', 'info');
        } catch {
            setProjectName(project.name);
            showNotification('Could not update project name', 'error');
        }
    };

    const handleImageUpdate = async (image: File) => {
        if (!project) {
            return;
        }

        try {
            await updateProject({ id: project.id, image });
            showNotification('Project image updated', 'info');
        } catch {
            showNotification('Could not update project image', 'error');
        }
    };

    const { roundPlan } = useRoundPlan(project?.application_manager_project_id);
    const isGenresEnabled = useFeatureFlags().isEnabled('rou_1766_project_genres');
    const isProjectSharingEnabled = useFeatureFlags().isEnabled('project_sharing');
    const isReceived = typeof project?.owned_by === 'number';

    return (
        <div className={styles.projectInfo}>
            <ImageUpload
                alt="Project image"
                className={styles.brandImage}
                isLoading={isUpdateLoading}
                value={project?.image}
                onUpload={handleImageUpdate}
            />

            <div className={styles.projectDetails}>
                <div>
                    <AutoGrowthInput
                        isLoading={isLoading}
                        type="text"
                        containerClassName={styles.projectNameInputContainer}
                        value={projectName}
                        onChange={(e) => {
                            setProjectName(e.target.value);
                        }}
                        onBlur={handleProjectNameSubmit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.currentTarget.blur();
                            }
                        }}
                    />

                    <p className={styles.projectSubTitle}>
                        <SkeletonizedValue
                            containerClassName={styles.projectSubTitle}
                            isInitialized={isBrandSuccess && (isReceived || isTeamSuccess)}
                        >
                            {isReceived ? brand?.name : `${brand?.name} | ${team?.name}`}
                        </SkeletonizedValue>
                    </p>
                </div>

                {isProjectSharingEnabled && <ProjectRunBy />}
            </div>

            <HeaderSummary className={styles.headerSummary}>
                <HeaderSummary.Item>
                    <HeaderSummary.Label>Run dates</HeaderSummary.Label>
                    <HeaderSummary.Value isLoading={isLoading}>
                        {project?.start_date && project?.end_date ? (
                            <RunDates
                                startDate={moment(project.start_date).format('DD MMM YYYY')}
                                endDate={moment(project.end_date).format('DD MMM YYYY')}
                            />
                        ) : (
                            '-'
                        )}
                    </HeaderSummary.Value>
                </HeaderSummary.Item>

                <HeaderSummary.Item>
                    <HeaderSummary.Label>Total cost</HeaderSummary.Label>
                    <HeaderSummary.Value isLoading={isLoading}>{cost}</HeaderSummary.Value>
                </HeaderSummary.Item>

                <HeaderSummary.Item>
                    <HeaderSummary.Label>Planners</HeaderSummary.Label>
                    <HeaderSummary.Value className={styles.teamContainer}>
                        {areUsersLoading &&
                            new Array(3)
                                .fill(undefined)
                                .map((_, i) => <Skeleton circle className={styles.userBadge} key={i} />)}

                        {!areUsersLoading &&
                            (users?.results.length
                                ? users?.results.map((user, index) => (
                                      <CircleBadge key={index} className={styles.userBadge} tooltip={user.name}>
                                          {getUserNameInitials(user.name)}
                                      </CircleBadge>
                                  ))
                                : '-')}
                    </HeaderSummary.Value>
                </HeaderSummary.Item>

                {isGenresEnabled && !!project?.genres.length && (
                    <HeaderSummary.Item>
                        <HeaderSummary.Label>Genres</HeaderSummary.Label>
                        <HeaderSummary.Value className={styles.genres}>
                            {project.genres.map((genre) => (
                                <span key={genre.value} className={styles.genre}>
                                    {genre.label}
                                </span>
                            ))}
                        </HeaderSummary.Value>
                    </HeaderSummary.Item>
                )}

                {roundPlan && (
                    <ProtectedByUserGroups groups={['user_admin']} shouldMatch="all">
                        <HeaderSummary.Item className={styles.rightSideItem}>
                            <HeaderSummary.Label>Round Plan</HeaderSummary.Label>
                            <HeaderSummary.Value>
                                <RoundPlan plan={roundPlan} />
                            </HeaderSummary.Value>
                        </HeaderSummary.Item>
                    </ProtectedByUserGroups>
                )}

                <ReportLink className={styles.rightSideItem} />

                {platform !== 'youtube' && (
                    <SongCard className={styles.rightSideItem} platform={platform} onSongClick={onSongClick} />
                )}
            </HeaderSummary>
        </div>
    );
};

export default ProjectData;
